import { useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { SPEEDS } from '../../state/protocol';
import { fmtClock, fmtSimTime } from './format';

const SLOT_KEY = (slot: number): string => `setpoint.save.${slot}`;

export function SimControls(): JSX.Element {
  const running = useEngine((s) => s.running);
  const speed = useEngine((s) => s.speed);
  const stats = useEngine((s) => s.stats);
  const simTime = useEngine((s) => s.snap?.simTime ?? 0);
  const seed = useEngine((s) => s.seed);
  const scenarioId = useEngine((s) => s.scenarioId);
  const plant = useEngine((s) => s.plant);
  const difficulty = useEngine((s) => s.difficulty);
  const play = useEngine((s) => s.play);
  const pause = useEngine((s) => s.pause);
  const setSpeed = useEngine((s) => s.setSpeed);
  const stepOnce = useEngine((s) => s.stepOnce);
  const requestSave = useEngine((s) => s.requestSave);
  const boot = useEngine((s) => s.boot);
  const [saved, setSaved] = useState<string | null>(null);

  const save = async (slot: number): Promise<void> => {
    const data = await requestSave();
    try {
      localStorage.setItem(SLOT_KEY(slot), data);
      setSaved(`Saved to slot ${slot} (${(data.length / 1024).toFixed(0)} kB)`);
    } catch {
      setSaved('Save failed — browser storage is full.');
    }
    setTimeout(() => setSaved(null), 2600);
  };

  const load = (slot: number): void => {
    const data = localStorage.getItem(SLOT_KEY(slot));
    if (!data || !plant) {
      setSaved(`Slot ${slot} is empty.`);
      setTimeout(() => setSaved(null), 2200);
      return;
    }
    boot({ plantId: plant.id, scenarioId, seed, difficulty, save: data });
  };

  return (
    <div
      className="hmi-controls"
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: 'var(--panel)', borderTop: '1px solid var(--line-dim)' }}
    >
      <button onClick={() => (running ? pause() : play())} style={{ width: 74 }} title="Space">
        {running ? '❚❚ Pause' : '▶ Run'}
      </button>
      <button onClick={() => stepOnce(600)} disabled={running} title="Advance one simulated minute">
        +1 min
      </button>
      <div style={{ display: 'flex', gap: 2 }}>
        {SPEEDS.map((s) => (
          <button key={s} className={speed === s ? 'on' : ''} onClick={() => setSpeed(s)} style={{ minWidth: 40 }} title={`${s}× (keys 1–4)`}>
            {s}×
          </button>
        ))}
      </div>

      <span className="num" style={{ marginLeft: 6, fontSize: 14 }}>
        {fmtClock(simTime)}
      </span>
      <span className="num muted hmi-optional" style={{ fontSize: 11 }}>
        elapsed {fmtSimTime(simTime)}
      </span>

      {stats.behind && running && (
        <span style={{ color: 'var(--pri-medium)', fontSize: 11.5 }} title="The engine cannot keep up with the requested speed; the simulation is running slower than asked. Results are unaffected.">
          ⚠ sim behind ({stats.achievedSpeed.toFixed(0)}×)
        </span>
      )}

      <span style={{ flex: 1 }} />

      {saved && <span className="muted" style={{ fontSize: 11.5 }}>{saved}</span>}

      <span
        className="num muted hmi-optional"
        style={{ fontSize: 11, cursor: 'pointer' }}
        title="Click to copy. The same seed replays the same shift."
        onClick={() => void navigator.clipboard?.writeText(String(seed))}
      >
        seed {seed}
      </span>
      <span className="num muted hmi-optional" style={{ fontSize: 11 }} title="Median cost of one simulated step">
        {stats.stepMicros.toFixed(0)} µs/step
      </span>

      {[1, 2, 3].map((slot) => (
        <span key={slot} style={{ display: 'flex', gap: 1 }}>
          <button style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => void save(slot)} title={`Save to slot ${slot}`}>
            S{slot}
          </button>
          <button style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => load(slot)} title={`Load slot ${slot}`}>
            L
          </button>
        </span>
      ))}
    </div>
  );
}
